import React, { useEffect } from "react";
import { InputBase, CircularProgress } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { cn } from "@/lib/utils";

interface Chat8InputAreaProps {
  input: string;
  setInput: (input: string) => void;
  sending: boolean;
  isConnected: boolean;
  onSubmit: () => void;
  hasPerguntasRespostas?: boolean;
}

export const Chat8InputArea: React.FC<Chat8InputAreaProps> = ({
  input,
  setInput,
  sending,
  isConnected,
  onSubmit,
  hasPerguntasRespostas = false,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  // ✅ Lógica corrigida: botão habilitado se conectado, não enviando e tem texto
  // O input sempre permite digitação, mas o botão só funciona quando há conexão
  const isButtonEnabled = isConnected && !sending && input.trim();

  useEffect(() => {
    if (isConnected && !sending) {
      setInput("");
    }
  }, [isConnected, sending]);

  return (
    <footer
      id="chat8-input-container"
      className="chat8-input-container chat8-message-input w-full border-t border-slate-600 bg-slate-900 flex items-center px-4 py-2 gap-2 relative"
      role="contentinfo"
      aria-label="Área de entrada de mensagem"
      data-testid="chat8-input-container"
    >
      <form
        id="chat8-message-form"
        onSubmit={handleSubmit}
        role="form"
        aria-label="Formulário de envio de mensagem"
        className="chat8-message-form flex items-center gap-2 w-full"
        data-testid="chat8-message-form"
      >
        <label
          id="chat8-message-label"
          htmlFor="chat8-message-input"
          className="sr-only chat8-message-label"
          data-testid="chat8-message-label"
        >
          Digite sua mensagem
        </label>
        <InputBase
          id="chat8-message-input"
          className="chat8-message-input flex-1 w-full px-4 py-3 text-sm leading-relaxed text-slate-100 bg-transparent border-none outline-none resize-none font-inherit"
          placeholder={
            isConnected
              ? "Digite sua mensagem aqui... (Ctrl+Enter para enviar)"
              : "Digite sua mensagem... (aguardando conexão)"
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          maxRows={4}
          // ✅ SEMPRE habilitado para digitação - nunca desabilitar o input
          disabled={false}
          aria-label="Campo de mensagem"
          aria-describedby="chat8-input-help"
          data-testid="chat8-message-input"
          sx={{
            "& .MuiInputBase-input": {
              color: "#f1f5f9",
              "&::placeholder": {
                color: isConnected ? "#64748b" : "#94a3b8",
                opacity: 1,
              },
            },
          }}
        />

        <div
          id="chat8-input-help"
          className="sr-only chat8-input-help"
          data-testid="chat8-input-help"
        >
          {isConnected
            ? "Pressione Enter para enviar ou Shift+Enter para nova linha"
            : "Aguardando conexão com o servidor"}
        </div>

        <button
          id="chat8-send-button"
          type="submit"
          disabled={!isButtonEnabled}
          aria-label={sending ? "Enviando mensagem..." : "Enviar mensagem"}
          className={cn(
            "chat8-send-button flex items-center justify-center p-3 min-w-12 h-12 text-white border-none rounded-lg transition-all duration-200 text-lg font-semibold",
            isButtonEnabled 
              ? "bg-blue-500 hover:bg-blue-600 cursor-pointer" 
              : "bg-slate-700 cursor-not-allowed"
          )}
          data-testid="chat8-send-button"
        >
          {sending ? (
            <div
              id="chat8-send-loading"
              className="chat8-send-loading flex items-center justify-center"
              data-testid="chat8-send-loading"
            >
              <CircularProgress
                id="chat8-loading-spinner"
                className="chat8-loading-spinner"
                data-testid="chat8-loading-spinner"
                size={20}
                thickness={2}
                sx={{
                  color: "#ffffff",
                }}
              />
            </div>
          ) : (
            <SendIcon
              id="chat8-send-icon"
              className="chat8-send-icon text-xl"
              data-testid="chat8-send-icon"
            />
          )}
        </button>
      </form>

      {/* ✅ Indicador visual de status de conexão */}
      {!isConnected && (
        <div
          id="chat8-connection-warning"
          className="chat8-connection-warning absolute bottom-1 left-4 text-xs text-slate-400 italic"
          data-testid="chat8-connection-warning"
          role="status"
          aria-live="polite"
        >
          ⚠️ Aguardando conexão...
        </div>
      )}
    </footer>
  );
};
